import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import ctaBg from '../landing/assets/about_us_4.webp';
import iconApple from '../landing/assets/Apple.png';
import iconGoogle from '../landing/assets/Playstore.png';
import sportsAnalyticsFeaturesBg from '../landing/assets/sports_analytics_features_bg.webp';
import sportsAnalyticsHeroBg from '../landing/assets/sports_analytics_hero_bg.webp';
import sportsAnalyticsWhyBg from '../landing/assets/sports_analytics_why_bg.webp';
import sportsAnalyticsIcon4 from '../landing/assets/svgs/dynamic_icon.svg';
import sportsAnalyticsIcon1 from '../landing/assets/svgs/sports_analytics_icon_1.svg';
import sportsAnalyticsIcon2 from '../landing/assets/svgs/sports_analytics_icon_2.svg';
import sportsAnalyticsIcon3 from '../landing/assets/svgs/sports_analytics_icon_3.svg';
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import CheckBadgeIcon from "../landing/icons/CheckBadgeIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";

const FAQs = [
    {
        q: "What is a sports analytics platform for clubs?",
        a: <>A sports analytics platform for clubs is software that helps teams collect, track, and analyze performance data. It gives clubs a clear focus on players’ performance, team strategies, and match results to support informed decisions.</>,
    },
    {
        q: "How does sports analytics software improve team performance?",
        a: <>Sports performance analytics software uses tracking data from training sessions and matches to highlight strengths and areas for improvement. Coaches can use performance analysis to adjust training plans for athletes and improve overall team performance.</>,
    },
    {
        q: "What kind of data can be analyzed using sports analytics software?",
        a: <>The platform analyzes player performance, match statistics, training data, video analysis, and team metrics. This data helps clubs understand patterns, monitor progress, and gain actionable insights.</>,
    },
    {
        q: "Can coaches and coaching staff easily use this platform?",
        a: <>Yes, the sports statistical analytics software is designed for coaches, trainers, and opponent analysis. Simple dashboards and reports make it easy to discover data, provide feedback, and plan winning strategies.</>,
    },
    {
        q: "Does the platform support live scoring and match analysis?",
        a: <>Yes, live scoring can be integrated with <a href="https://scorepal.co/sports-league-management-software/" className={"not-hover:underline"}>sports league software</a> or tournament league software. This allows clubs to analyze match-day performance in real time and track accurate stats.</>,
    },
    {
        q: "How does sports analytics help clubs gain a competitive edge?",
        a: <>By using data-driven insights and powerful tools, clubs can improve player development, analyze opponents, refine team strategies, and make smarter decisions for the recruitment process that lead to better results and long-term success.</>,
    },
];

export const Route = createFileRoute('/sports-analytics-software-for-clubs')({
    component: RouteComponent,
})

function RouteComponent() {
    const features = [
        {
            icon:        sportsAnalyticsIcon4,
            title:       'Analyze Player Data',
            alt:         'dynamic icon',
            description: 'Track key metrics across training sessions, matches, and seasons. ScorePal centralizes player data so coaches can easily monitor progress, identify strengths, and pinpoint areas for improvement.',
        },
        {
            icon:        sportsAnalyticsIcon1,
            title:       'Performance Tracking Made Easy',
            alt:         'sports analytics',
            description: 'From fitness and skill evaluations to in-game statistics, ScorePal captures the insights that matter. Visual dashboards make it simple to compare performance trends, evaluate players, and optimize training plans.',
        },
        {
            icon:        sportsAnalyticsIcon2,
            title:       'Data-Driven Decision Making',
            alt:         'sports analytics',
            description: 'Empower your coaching staff with actionable analytics. Make informed choices about lineups, tactics, and development pathways based on real performance data—not guesswork.',
        },
        {
            icon:        sportsAnalyticsIcon3,
            title:       'All-in-One Analytics Software',
            alt:         'sports analytics',
            description: 'ScorePal, sports data analytics software, integrates seamlessly with your existing league or event data, ensuring accurate, consistent insights across your entire club. Access reports, charts, and historical data anytime from any device.',
        },
    ];

    const whyChooseItems = [
        'Centralized player and team data',
        'Easy-to-understand dashboards and reports',
        'Supports long-term development planning',
        'Enhances coaching effectiveness',
        'Flexible for clubs, academies, and performance programs',
    ];

    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context":   "https://schema.org",
                "@type":      "FAQPage",
                "mainEntity": [
                    {
                        "@type":          "Question",
                        "name":           "What is a sports analytics platform for clubs?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "A sports analytics platform for clubs is software that helps teams collect, track, and analyze performance data. It gives clubs a clear focus on players’ performance, team strategies, and match results to support informed decisions."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "How does sports analytics software improve team performance?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Sports performance analytics software uses tracking data from training sessions and matches to highlight strengths and areas for improvement. Coaches can use performance analysis to adjust training plans for athletes and improve overall team performance."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "What kind of data can be analyzed using sports analytics software?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "The platform analyzes player performance, match statistics, training data, video analysis, and team metrics. This data helps clubs understand patterns, monitor progress, and gain actionable insights."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Can coaches and coaching staff easily use this platform?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Yes, the sports statistical analytics software is designed for coaches, trainers, and opponent analysis. Simple dashboards and reports make it easy to discover data, provide feedback, and plan winning strategies."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Does the platform support live scoring and match analysis?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Yes, live scoring can be integrated with sports league software or tournament league software. This allows clubs to analyze match-day performance in real time and track accurate stats."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "How does sports analytics help clubs gain a competitive edge?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "By using data-driven insights and powerful tools, clubs can improve player development, analyze opponents, refine team strategies, and make smarter decisions for the recruitment process that lead to better results and long-term success."
                        }
                    }
                ]
            }} />
            <JsonLd id={`page${Route.id}/app`} item={{
                "@context":            "https://schema.org",
                "@type":               "SoftwareApplication",
                "name":                "Sports Analytics Software for Clubs",
                "url":                 "https://scorepal.co/sports-analytics-software-for-clubs/",
                "applicationCategory": "SportsApplication",
                "operatingSystem":     ["Web", "Android", "iOS"],
                "description":         "Analyze player performance, match statistics, and rankings with advanced analytics tools built for sports clubs and coaches. Proven performance over the past 2 years.",
                "offers":              {
                    "@type":         "Offer",
                    "price":         "0",
                    "priceCurrency": "USD"
                }
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "Sports Analytics Software for Clubs", item: "https://scorepal.co/sports-analytics-software-for-clubs/"}]} />
            <title>Sports Analytics Software for Clubs | Performance & Insights | ScorePal</title>
            <meta name="description" content="Sports analytics software for clubs with performance insights, live scoring data, league tracking, and reporting tools to improve team results." />
            <link rel="canonical" href={`${config.publicUrl}/sports-analytics-software-for-clubs/`} />

            <section className="relative overflow-hidden p-4">
                <div
                    className="flex flex-col min-h-screen justify-between relative pb-16 rounded-4xl bg-[#EDEEF4] bg-top bg-cover bg-no-repeat saturate-[0.87]"
                    style={{
                        backgroundImage: `url(${sportsAnalyticsHeroBg})`
                    }}
                >
                    <div className="absolute inset-0 bg-linear-to-r from-black/60 via-black/30 to-transparent rounded-4xl" />

                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8 flex-1 flex flex-col justify-center relative z-10">
                        <h1 className="font-bebas text-white uppercase text-[clamp(40px,6vw,72px)] leading-none font-bold tracking-wide mb-4">
                            SPORTS ANALYTICS <br />SOFTWARE FOR CLUBS
                        </h1>

                        <p className="font-inter text-white/90 text-base lg:text-xl mb-8 max-w-2xl leading-relaxed">
                            Turn match data into actionable insights with ScorePal’s analytics tools for performance tracking, results analysis, and smarter decision-making.
                        </p>

                        <Link
                            to={"/signup/player/" as any}
                            className="flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 w-fit font-semibold hover:bg-white transition"
                        >
                            <span className="font-inter text-base font-medium">Lets Get Started</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>

                        <div className="flex flex-wrap gap-4 mt-16 lg:mt-60">
                            <a
                                href="https://play.google.com/store/apps/details?id=co.scorepal.mobile&pcampaignid=web_share"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block"
                            >
                                <div className="bg-white/10 rounded-xl px-6 py-3 flex items-center gap-3 hover:bg-gray-100 transition">
                                    <img src={iconGoogle} alt="Google Play" className="h-8" />
                                    <div className="text-left">
                                        <div className="text-white/70 text-xs">GET IT ON</div>
                                        <div className="text-white font-semibold text-sm">Google Play</div>
                                    </div>
                                </div>
                            </a>

                            <a
                                href="https://apps.apple.com/us/app/score-pal/id6745509167"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block"
                            >
                                <div className="bg-white/10 rounded-xl px-6 py-3 flex items-center gap-3 hover:bg-gray-900 transition">
                                    <img src={iconApple} alt="App Store" className="h-8" />
                                    <div className="text-left">
                                        <div className="text-white/70 text-xs">Download on the</div>
                                        <div className="text-white font-semibold text-sm">App Store</div>
                                    </div>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start">
                        <div>
                            <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-6">
                                UNLOCK POWERFUL PERFORMANCE INSIGHTS FOR <span className="text-[#082F8A]">YOUR CLUB</span>
                            </h2>

                            <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed">
                                Transform the way your club understands and improves performance with <a href="https://scorepal.co/sports-league-management-software/" className={"not-hover:underline"}>ScorePal's</a> sports analytics platform. Designed for clubs of all sizes, ScorePal delivers deep insights into player development, team performance, and long-term growth—helping you make smarter, data-driven decisions.
                            </p>
                        </div>

                        <div className="hidden lg:block">
                            <img
                                src={sportsAnalyticsFeaturesBg}
                                alt="Sports analytics"
                                className="w-full h-auto rounded-3xl object-cover"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-12">
                        {features.map((feature, index) => (
                            <div key={index} className="bg-white rounded-2xl p-6 shadow-sm">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-full bg-[#EDEEF4] flex items-center justify-center shrink-0">
                                        <img src={feature.icon} alt="" className="w-6 h-6" />
                                    </div>
                                    <h3 className="font-inter text-[#082F8A] font-semibold text-base">
                                        {feature.title}
                                    </h3>
                                </div>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">
                                    {feature.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <div className="flex justify-center">
                <Link
                    to={"/signup/player/" as any}
                    className="flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 w-fit font-semibold hover:bg-white transition"
                >
                    <span className="font-inter text-base font-medium">Lets Get Started</span>
                    <div className="bg-[#082F8A] rounded-full p-1">
                        <ArrowUpRightIcon className="text-white" size={28} />
                    </div>
                </Link>
            </div>

            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
                        <div>
                            <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-8">
                                Why Choose ScorePal Analytics as a<br />Sports Analytics Platform for Clubs?
                            </h2>

                            <ul className="space-y-4">
                                {whyChooseItems.map((item, index) => (
                                    <li key={index} className="flex items-center gap-4">
                                        <CheckBadgeIcon size={24} />
                                        <span className="font-inter text-[#1a1a2e] text-base">
                                            {item}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="hidden lg:block">
                            <img
                                src={sportsAnalyticsWhyBg}
                                alt="Why choose ScorePal Analytics"
                                className="w-full h-auto rounded-3xl object-cover"
                            />
                        </div>

                        <div className="block lg:hidden">
                            <img
                                src={sportsAnalyticsWhyBg}
                                alt="Why choose ScorePal Analytics"
                                className="w-full h-auto rounded-3xl object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-8 lg:py-16">
                <div className="container mx-auto px-6 lg:px-12">
                    <div
                        className="rounded-3xl py-16 lg:py-24 px-8 text-center bg-top bg-cover bg-no-repeat"
                        style={{backgroundImage: `url(${ctaBg})`}}
                    >
                        <h2 className="font-bebas text-white uppercase text-[clamp(28px,4vw,48px)] leading-tight font-bold tracking-wide mb-4">
                            TURN DATA INTO YOUR COMPETITIVE ADVANTAGE
                        </h2>
                        <p className="font-inter text-white/90 text-base lg:text-lg mb-8 max-w-3xl mx-auto leading-relaxed">
                            ScorePal's sports analytics platform helps clubs unlock their full potential. Improve decision-making, elevate player performance, and build stronger, more strategic programs powered by real data.
                            <br /><br />
                            Start making smarter decisions with the ScorePal sports analytics platform for clubs today.
                        </p>
                        <Link
                            to={"/signup/player/" as any}
                            className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition"
                        >
                            <span className="font-inter text-base font-medium">Lets Get Started</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>
                    <FaqComponent title={"Faqs About Sports Analytics Platform for Clubs"} faqs={FAQs} />
                </div>
            </section>

            <FooterSection />
        </div>
    );
}
